import { TransportProvider } from "@connectrpc/connect-query";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import "dayjs/locale/ja";
import { AppErrorBoundary, type Translate } from "@viajero/shared-ui/error-boundary";
import { ThemeProvider } from "@viajero/shared-ui/theme";
import { UpdateSnackbar } from "@viajero/shared-ui/update-snackbar";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { createI18n, I18nProvider } from "@viajero/shared-ui/i18n";
import { defaultLanguage, STORAGE_LANG_KEY, supportedLanguages } from "@/i18n/constants";
import { transport } from "@/lib/client";
import { QueryProvider } from "@/lib/queryClient";
import { guestTheme } from "@/theme/guestTheme";

// AppErrorBoundary の英語既定文言のうち、guest 固有の 2 次案内 (メールでの予約確認) を含めるもの。
// 再読み込みで直らない場合に備え、メールで予約状況を確認できる旨を添える
// (docs/design/ゲストアプリ_エラー表示_詳細設計.md §7)。
const errorFallbacks: Record<string, string> = {
  "errorPage.unexpectedDescription":
    "Please try reloading the page. If the problem persists, please check the email sent to your registered address. You can confirm your reservation details and status there.",
};

const i18n = createI18n({ defaultLanguage, supportedLanguages, storageKey: STORAGE_LANG_KEY });

// i18next の init() は上の createI18n で同期的に走るが、翻訳リソースの取得は非同期。
// 初期化前や locale JSON の取得失敗時に翻訳キーがそのまま画面に出ないよう、英語固定の文言を
// 必ず defaultValue として持たせる。i18next インスタンスを直接呼ぶため、I18nProvider の外側
// (= 翻訳が使えない状態) でも文字として読める。
const translateError: Translate = (key, fallback) => {
  const defaultValue = errorFallbacks[key] ?? fallback;
  return i18n.isInitialized ? i18n.t(key, { defaultValue }) : defaultValue;
};

/**
 * MUI X のピッカーに、表示言語に合わせた日付ロケールを渡す。利用日のカレンダーは月名・曜日を
 * 表示するため、アプリの表示言語と揃える必要がある (JPTP_CANSEI-1640)。
 * ja 以外は dayjs 既定の en ロケールで描かれるため、追加の import は ja のみでよい。
 */
function DateLocalizationProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={i18n.resolvedLanguage === "ja" ? "ja" : "en"}
    >
      {children}
    </LocalizationProvider>
  );
}

/**
 * アプリ全体に必要な Provider 群を 1 箇所に集約する。
 *
 * ゲストアプリは Cognito 認証なしの公開 SPA のため AuthProvider / RequireAuth は置かない。
 * 「セッション」は URL の案件アクセストークン 1 本で、concierge の lib/auth は参照しない
 * (docs/design/ゲストアプリの設計.md §3.2 #2)。
 *
 * i18n は shared-ui の I18nProvider を使い、guest 独自の `@/i18n/constants` (既定 en / en・ja の
 * 2 言語) を createI18n に渡す (同 §3.2 #4)。
 * TransportProvider / QueryProvider は guest.v1 の RPC 呼び出し (connect-query) に必要。
 * DateLocalizationProvider は MUI X のピッカー (利用日時) に日付アダプタを供給する。
 *
 * `AppErrorBoundary` は ThemeProvider の内側 (MUI コンポーネントを guestTheme で描くため) かつ
 * I18nProvider の外側に置き、i18n の初期化を含むアプリ全体の描画例外を受ける
 * (JPTP_CANSEI-1525)。フォールバックの文言は i18next インスタンス直呼びのため context は不要。
 * `UpdateSnackbar` は useTranslation を使うので I18nProvider の内側、かつ RPC の状態に依らず
 * 常時表示するため QueryProvider の外側に置く (manager / concierge と同じ位置付け)。
 */
export function AppProvider({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider theme={guestTheme}>
      <AppErrorBoundary translate={translateError}>
        <I18nProvider>
          <DateLocalizationProvider>
            <TransportProvider transport={transport}>
              <QueryProvider>{children}</QueryProvider>
            </TransportProvider>
          </DateLocalizationProvider>
          <UpdateSnackbar />
        </I18nProvider>
      </AppErrorBoundary>
    </ThemeProvider>
  );
}
